import { AnimatePresence, motion } from 'motion/react'
import type { ShopLanguage } from '../../../i18n'
import type { SelectOption } from '../../../components/SelectCustom'
import { customerMotionTransition } from '../customer-area'
import {
  type OrderDateFilterOption,
  type OrderSortOption,
  type OrderVisualStatus,
  OrdersToolbarSkeleton,
} from './ordersListShared'
import type { ActiveFilterChip, TranslateFn } from './ordersListSectionTypes'
import { OrdersActiveFilterChips } from './OrdersActiveFilterChips'
import { OrdersFiltersPanel } from './OrdersFiltersPanel'
import { OrdersToolbarTopControls } from './OrdersToolbarTopControls'

type OrdersToolbarSectionProps = {
  reducedMotion: boolean
  loading: boolean
  language: ShopLanguage
  searchValue: string
  sortBy: OrderSortOption
  filtersOpen: boolean
  dateFilter: OrderDateFilterOption
  customDateFrom: string
  customDateTo: string
  statusFilters: OrderVisualStatus[]
  priceMin: string
  priceMax: string
  sortOptions: SelectOption[]
  dateFilterOptions: SelectOption[]
  activeFilterChips: ActiveFilterChip[]
  filteredCount: number
  onSearchChange: (value: string) => void
  onToggleFilters: () => void
  onSortChange: (value: OrderSortOption) => void
  onDateFilterChange: (value: OrderDateFilterOption) => void
  onCustomDateFromChange: (value: string) => void
  onCustomDateToChange: (value: string) => void
  onToggleStatusFilter: (status: OrderVisualStatus, checked: boolean) => void
  onPriceMinChange: (value: string) => void
  onPriceMaxChange: (value: string) => void
  onClearAllFilters: () => void
  t: TranslateFn
}

export const OrdersToolbarSection = ({
  reducedMotion,
  loading,
  language,
  searchValue,
  sortBy,
  filtersOpen,
  dateFilter,
  customDateFrom,
  customDateTo,
  statusFilters,
  priceMin,
  priceMax,
  sortOptions,
  dateFilterOptions,
  activeFilterChips,
  filteredCount,
  onSearchChange,
  onToggleFilters,
  onSortChange,
  onDateFilterChange,
  onCustomDateFromChange,
  onCustomDateToChange,
  onToggleStatusFilter,
  onPriceMinChange,
  onPriceMaxChange,
  onClearAllFilters,
  t,
}: OrdersToolbarSectionProps) => (
  <header className="ca-theme-transition border-b border-(--color-border-default) bg-(--color-bg-card) px-4 py-4 sm:px-5">
    {loading ? (
      <motion.div
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
        animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
        transition={customerMotionTransition}
      >
        <OrdersToolbarSkeleton />
      </motion.div>
    ) : (
      <>
        <OrdersToolbarTopControls
          reducedMotion={reducedMotion}
          searchValue={searchValue}
          sortBy={sortBy}
          filtersOpen={filtersOpen}
          sortOptions={sortOptions}
          onSearchChange={onSearchChange}
          onToggleFilters={onToggleFilters}
          onSortChange={onSortChange}
          t={t}
        />

        <AnimatePresence initial={false}>
          {filtersOpen ? (
            <OrdersFiltersPanel
              reducedMotion={reducedMotion}
              language={language}
              dateFilter={dateFilter}
              customDateFrom={customDateFrom}
              customDateTo={customDateTo}
              statusFilters={statusFilters}
              priceMin={priceMin}
              priceMax={priceMax}
              dateFilterOptions={dateFilterOptions}
              filteredCount={filteredCount}
              onDateFilterChange={onDateFilterChange}
              onCustomDateFromChange={onCustomDateFromChange}
              onCustomDateToChange={onCustomDateToChange}
              onToggleStatusFilter={onToggleStatusFilter}
              onPriceMinChange={onPriceMinChange}
              onPriceMaxChange={onPriceMaxChange}
              onClearAllFilters={onClearAllFilters}
              t={t}
            />
          ) : null}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {activeFilterChips.length > 0 ? (
            <OrdersActiveFilterChips reducedMotion={reducedMotion} chips={activeFilterChips} />
          ) : null}
        </AnimatePresence>
      </>
    )}
  </header>
)
